import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ClipboardCheck } from 'lucide-react'
import { getCorrectiveActions, type CorrectiveAction } from '../../lib/services/correctiveActions'
import { useTier } from '../../hooks/useTier'

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-[var(--brand-primary)]',
  in_progress: 'bg-amber-500',
  completed: 'bg-emerald-500',
  verified: 'bg-teal-500',
  overdue: 'bg-rose-500',
}

const STATUS_BADGE: Record<string, string> = {
  open: 'badge-scheduled',
  in_progress: 'badge-in-progress',
  completed: 'badge-released',
  verified: 'badge-released',
  overdue: 'badge-archived',
}

function formatToken(value: string | null | undefined): string {
  if (!value) return 'Unassigned'
  return value.replace(/_/g, ' ')
}

function formatAssignee(action: CorrectiveAction): string {
  if (action.assigned_to?.email) {
    return action.assigned_to.role
      ? `${action.assigned_to.email} (${formatToken(action.assigned_to.role)})`
      : action.assigned_to.email
  }
  return action.assigned_to_user_id ? 'Assigned user' : 'Unassigned'
}

function formatVisitContext(action: CorrectiveAction): string {
  if (!action.visit) return 'No visit context'
  const type = action.visit.visit_type ? `${formatToken(action.visit.visit_type)} visit` : 'Visit context'
  const status = action.visit.status ? `Status: ${formatToken(action.visit.status)}` : 'Status: not set'
  return `${type} | ${status}`
}

function formatFacilityGroup(action: CorrectiveAction): string {
  if (!action.visit?.facility_id) return 'Unassigned'
  const visitType = action.visit.visit_type ? `${formatToken(action.visit.visit_type)} visit` : 'facility visit'
  return `${visitType} action group`
}

function CapaSummaryBar({ actions }: { actions: CorrectiveAction[] }) {
  const counts: Record<string, number> = { open: 0, in_progress: 0, completed: 0, verified: 0, overdue: 0 }
  for (const a of actions) {
    counts[a.status] = (counts[a.status] ?? 0) + 1
  }
  const total = actions.length
  if (total === 0) return null

  return (
    <div className="v11-section p-4">
      <div className="mb-2 flex h-2.5 overflow-hidden rounded-full bg-slate-100">
        {Object.entries(counts).map(([status, count]) => {
          if (count === 0) return null
          return (
            <div
              key={status}
              className={`${STATUS_COLORS[status] ?? 'bg-slate-300'}`}
              style={{ width: `${(count / total) * 100}%` }}
            />
          )
        })}
      </div>
      <div className="flex flex-wrap gap-3">
        {Object.entries(counts).map(([status, count]) => (
          <span key={status} className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
            <span className={`h-2 w-2 rounded-full ${STATUS_COLORS[status] ?? 'bg-slate-300'}`} />
            {status.replace('_', ' ')} ({count})
          </span>
        ))}
      </div>
    </div>
  )
}

export function ActionsListPage() {
  const tier = useTier()
  const { data, isLoading, error } = useQuery({
    queryKey: ['corrective-actions'],
    queryFn: () => getCorrectiveActions(),
  })

  const actions = data ?? []

  // Tier-aware heading
  const heading = tier === 'standalone' || tier === 'enterprise'
    ? 'My Tasks'
    : 'Corrective Actions'
  const subheading = tier === 'standalone'
    ? 'Your assigned compliance tasks with evidence requirements.'
    : 'Track owners, due dates, status, and linked evidence by permission.'

  // Group by facility for consultant tier
  const grouped = new Map<string, CorrectiveAction[]>()
  if (tier === 'consultant') {
    for (const a of actions) {
      const key = a.visit?.facility_id ?? 'unassigned'
      const list = grouped.get(key) ?? []
      list.push(a)
      grouped.set(key, list)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
          CAPA / Actions
        </p>
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">{heading}</h1>
        <p className="mt-1 text-sm text-slate-600">{subheading}</p>
      </header>

      <CapaSummaryBar actions={actions} />

      <section className="v11-section">
        <div className="v11-section-header">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">
            {tier === 'consultant' ? 'Facility Grouped Actions' : 'Action Queue'}
          </h2>
        </div>
        <div className="p-5">
          {isLoading && <p className="text-sm text-slate-500">Loading corrective actions...</p>}
          {error && <p className="text-sm text-red-600">{(error as Error).message}</p>}

          {!isLoading && !error && actions.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-500">
              No corrective actions found.
            </div>
          )}

          {/* Consultant: grouped by facility */}
          {tier === 'consultant' && Array.from(grouped.entries()).map(([facilityId, facilityActions]) => (
            <div key={facilityId} className="mb-4">
              <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                {formatFacilityGroup(facilityActions[0])}
              </h3>
              <div className="grid gap-3">
                {facilityActions.map((action) => (
                  <ActionCard key={action.action_id} action={action} />
                ))}
              </div>
            </div>
          ))}

          {/* Default: flat list */}
          {tier !== 'consultant' && (
            <div className="grid gap-3">
              {actions.map((action) => (
                <ActionCard key={action.action_id} action={action} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

function ActionCard({ action }: { action: CorrectiveAction }) {
  const badgeClass = STATUS_BADGE[action.status] ?? 'badge-scheduled'
  const isOverdue = action.status === 'overdue'

  return (
    <Link
      to={`/actions/${action.action_id}`}
      className={`v11-dashboard-card p-4 ${isOverdue ? 'border-rose-200 bg-rose-50/30' : ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <ClipboardCheck size={16} className={`shrink-0 ${isOverdue ? 'text-rose-500' : 'text-[var(--brand-primary)]'}`} />
          <p className="min-w-0 break-words text-sm font-bold text-slate-900">{action.title}</p>
        </div>
        <span className={`${badgeClass} shrink-0`}>{action.status.replace('_', ' ')}</span>
      </div>
      <div className="mt-2 grid gap-1 text-xs text-slate-500 sm:grid-cols-2">
        <p>Due: {action.due_date || 'Not set'}</p>
        <p>Owner: {formatToken(action.owner_role)}</p>
        <p className="break-words sm:col-span-2">Assignee: {formatAssignee(action)}</p>
        <p className="sm:col-span-2">{formatVisitContext(action)}</p>
      </div>
      {action.description ? <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-600">{action.description}</p> : null}
      <p className="mt-1 text-xs text-slate-500">
        Evidence: {action.evidence_links.length} linked{action.evidence_required ? ' (required)' : ''}
      </p>
    </Link>
  )
}
